// needed to smooth out cross browser behavior inconsistencies
import 'webrtc-adapter';
import { WebSocket } from 'partysocket';
import { checkNever, isDefined, Observable } from '@towns-protocol/sdk';
import { ethers } from 'ethers';
import { createSignatureMessage } from 'worker-common';
import { bin_toHexString } from '@towns-protocol/utils';
import { LiveErrorCode, LIVE_CONSTANTS, } from '@harmony/live-types';
import { LiveAudioManager, } from './LiveAudioManager';
const EMPTY_SPACE_STATE = {
    channels: {},
    state: {
        subscribers: { totalCount: 0, userIds: [] },
        updatedAt: 0,
    },
};
/**
 * LiveClient manages the WebSocket connection to the live-worker
 * and provides a typed API for real-time presence features.
 */
export class LiveClient {
    options;
    ws = null;
    _userId = undefined;
    messageHandlers = new Map();
    audioManager;
    _nonce = 0;
    _disconnectRequested = false;
    // Observable state for React hooks
    state$;
    userIntent = {
        subscriptions: [],
        cappedSubscriptions: [],
    };
    // Message event emitter for custom handling
    constructor(options) {
        this.options = options;
        // Initialize observable state
        console.log('[LiveClient ws>] Initializing state$');
        this.state$ = new Observable({
            ready: false,
            connectionStatus: 'disconnected',
            deviceState: undefined,
            devices: [],
            spaces: {},
            lastError: undefined,
            isReconnecting: false,
        });
    }
    /**
     * Get the current user ID (from ticket)
     */
    get userId() {
        return this._userId;
    }
    /** helper to get the current spaceId and channelId if you're live */
    get currentSpace() {
        if (this.state$.value.deviceState?.type === 'live') {
            return {
                spaceId: this.state$.value.deviceState.spaceId,
                channelId: this.state$.value.deviceState.channelId,
            };
        }
        return undefined;
    }
    /**
     * Connect to the live worker with automatic retry until disconnect() is called
     */
    async connect(opts) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.log('[LiveClient ws>] Already connected');
            return; // Already connected
        }
        this._disconnectRequested = false;
        let retryCount = 0;
        const maxRetryDelay = 30000; // 30 seconds max delay
        let connectResponse;
        do {
            if (this._disconnectRequested) {
                console.log('[LiveClient ws>] connect() aborted due to disconnect request');
                return;
            }
            try {
                connectResponse = await this._connect();
                break;
            }
            catch (error) {
                if (opts?.maxRetryCount !== undefined && retryCount >= opts.maxRetryCount) {
                    console.error(`[LiveClient ws>] connect() failed (attempt ${retryCount}), max retry count reached`, error);
                    this.disconnect();
                    this.updateState({
                        connectionStatus: 'error',
                        lastError: {
                            type: 'error_response',
                            code: LiveErrorCode.INTERNAL_ERROR,
                            message: error instanceof Error ? error.message : 'Connection failed',
                            timestamp: Date.now(),
                            nonce: this.getNonce(),
                        },
                    });
                    throw error;
                }
                retryCount++;
                const delay = Math.min(1000 * Math.pow(2, retryCount - 1), maxRetryDelay);
                console.error(`[LiveClient ws>] connect() failed (attempt ${retryCount}), retrying in ${delay}ms`, error);
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        } while (true);
        // update devices
        this.handleDevicesUpdate({ devices: connectResponse.devices });
        // Restore subscriptions from server - this triggers live_space_summary messages
        // to be sent for any existing subscriptions
        await this.restoreState();
        // sync our local state to how we are represented on the server
        if (this.state$.value.deviceState?.type === 'live') {
            console.log('[LiveClient ws>] After Connect - Joining channel:', this.state$.value.deviceState.spaceId, this.state$.value.deviceState.channelId);
            await this.joinChannel(this.state$.value.deviceState.spaceId, this.state$.value.deviceState.channelId);
        }
        this.updateState({ ready: true });
    }
    async _connect() {
        console.log('[LiveClient ws>] Connect');
        this.updateState({ connectionStatus: 'connecting' });
        // Clean up any existing WebSocket before creating a new one
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        try {
            // Get authentication ticket
            const urlProvider = async () => {
                console.log('[LiveClient ws>] Getting ticket');
                const ticket = await this.getTicket();
                this._userId = ticket.userId;
                // Create WebSocket with PartySocket for automatic reconnection
                // The ticket is a base64-encoded signed request that includes userId and deviceId
                const wsUrl = `${this.options.liveWorkerUrl.replace('http', 'ws')}/connect?ticket=${encodeURIComponent(ticket.ticket)}`;
                return wsUrl;
            };
            this.ws = new WebSocket(urlProvider, undefined, {
                startClosed: false,
                maxRetries: 5,
                minReconnectionDelay: 1000,
                maxReconnectionDelay: 10000,
                debug: true,
                ...this.options.wsOptions,
            });
            // Set up event handlers
            this.setupWebSocketHandlers();
            // Wait for connection to open
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this.ws?.close();
                    reject(new Error('Connection timeout'));
                }, 10000);
                const connectResponseHandler = this.onMessage((msg) => {
                    if (msg.type === 'connect_response') {
                        clearTimeout(timeout);
                        connectResponseHandler();
                        resolve(msg);
                    }
                });
                const errorHandler = (event) => {
                    clearTimeout(timeout);
                    connectResponseHandler();
                    this.ws?.removeEventListener('error', errorHandler);
                    console.error('[LiveClient ws>] WebSocket error waiting for connect response', event);
                    reject(new Error(`WebSocket error: ${event.message}`));
                };
                this.ws?.addEventListener('error', errorHandler);
            });
        }
        catch (error) {
            console.error('[LiveClient ws>] connect() failed', error);
            this.updateState({
                connectionStatus: 'error',
                lastError: {
                    type: 'error_response',
                    code: LiveErrorCode.INTERNAL_ERROR,
                    message: error instanceof Error ? error.message : 'Connection failed',
                    timestamp: Date.now(),
                    nonce: this.getNonce(),
                },
            });
            throw error;
        }
    }
    /**
     * Disconnect from the live worker
     */
    disconnect() {
        this._disconnectRequested = true;
        this.audioManager?.destroy();
        this.audioManager = undefined;
        this.updateState({ audioState: undefined });
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        console.log('[LiveClient ws>] Disconnect(), updating state');
        this.updateState({
            ready: false,
            connectionStatus: 'disconnected',
            deviceState: undefined,
            devices: [],
            spaces: {},
        });
    }
    /**
     * Update subscriptions to one or more spaces, pass empty array to unsubscribe from all spaces
     * subscriptions are limited to MAX_SPACE_SUBSCRIPTIONS, this function will take the first MAX_SPACE_SUBSCRIPTIONS spaces from the array
     */
    async updateSubscriptions(spaceIds, opts) {
        this.ensureConnected();
        // make sure we're always subscribed to the current space and intent space
        for (const spaceId of [this.currentSpace?.spaceId, opts?.intent?.spaceId].filter(isDefined)) {
            const index = spaceIds.indexOf(spaceId);
            if (index !== -1) {
                spaceIds.splice(index, 1);
            }
            spaceIds.unshift(spaceId);
        }
        // take the first MAX_SPACE_SUBSCRIPTIONS spaces from the array
        const cappedSpacedIds = spaceIds.slice(0, LIVE_CONSTANTS.MAX_SPACE_SUBSCRIPTIONS);
        this.userIntent.subscriptions = spaceIds;
        this.userIntent.cappedSubscriptions = cappedSpacedIds;
        const message = {
            type: 'subscriptions',
            spaceIds: cappedSpacedIds,
            nonce: this.getNonce(),
        };
        const response = await this.sendAndWaitForResponse(message, (msg) => msg.type === 'subscriptions_response');
        this.handleSubscriptionsResponse(response);
        return response;
    }
    /**
     * Join a channel (join stage or listeners)
     */
    async joinChannel(spaceId, channelId) {
        console.log('[LiveClient ws>] joinChannel', spaceId, channelId);
        this.ensureConnected();
        if (this.currentSpace?.channelId !== channelId) {
            if (this.audioManager || this.currentSpace) {
                console.warn('[LiveClient ws>] leaving the current channel');
                await this.leaveChannel();
            }
        }
        console.log('[LiveClient ws>] Joining channel:', spaceId, channelId);
        if (!this.userIntent.cappedSubscriptions.includes(spaceId)) {
            // the space isn't subscribed to yet, update the subscriptions to include it
            await this.updateSubscriptions(this.userIntent.subscriptions, {
                intent: { spaceId, channelId },
            });
        }
        const message = {
            type: 'join_channel',
            spaceId,
            channelId,
            nonce: this.getNonce(),
        };
        const joinChannelResponse = await this.sendAndWaitForResponse(message, (msg) => msg.type === 'join_channel_response', 10 * 1000);
        // some destructuring
        const { partytracksToken, audioTracks, devices, placement } = joinChannelResponse;
        // update devices
        this.handleDevicesUpdate({ devices });
        // Create audio manager using factory if provided, otherwise use default
        const audioManagerParams = {
            delegate: this,
            apiBase: this.options.liveWorkerUrl,
            spaceId,
            channelId,
            partytracksToken,
        };
        if (!this.audioManager) {
            this.audioManager = this.options.audioManagerFactory
                ? this.options.audioManagerFactory(audioManagerParams)
                : new LiveAudioManager(audioManagerParams);
            this.audioManager.state$.subscribe((state) => {
                this.updateState({ audioState: state });
            });
        }
        if (placement === 'stage') {
            await this.audioManager.initMic();
        }
        await this.audioManager.subscribeTracks(audioTracks);
        return joinChannelResponse;
    }
    /**
     * Leave the current channel
     */
    async leaveChannel() {
        console.log('[LiveClient ws>] leaveChannel');
        this.ensureConnected();
        this.audioManager?.destroy();
        this.audioManager = undefined;
        this.updateState({ audioState: undefined });
        const message = {
            type: 'leave_channel',
            nonce: this.getNonce(),
        };
        const leaveChannelResponse = await this.sendAndWaitForResponse(message, (msg) => msg.type === 'leave_channel_response');
        this.handleDevicesUpdate({ devices: leaveChannelResponse.devices });
        return leaveChannelResponse;
    }
    /**
     * Leave the stage (move from stage to listener)
     * Destroys the audio manager and moves user to listeners
     */
    async leaveStage() {
        console.log('[LiveClient ws>] leaveStage');
        this.ensureConnected();
        // stop the microphone
        await this.audioManager?.stopMic();
        const message = {
            type: 'leave_stage',
            nonce: this.getNonce(),
        };
        const leaveStageResponse = await this.sendAndWaitForResponse(message, (msg) => msg.type === 'leave_stage_response');
        return leaveStageResponse;
    }
    /**
     * Request to join the stage (move from listener to stage, or queue if full)
     * If there's room on stage, user is moved immediately and mic is initialized.
     * If stage is full, user is placed in stageRequests queue.
     */
    async joinStage() {
        console.log('[LiveClient ws>] joinStage');
        this.ensureConnected();
        const message = {
            type: 'join_stage',
            nonce: this.getNonce(),
        };
        const joinStageResponse = await this.sendAndWaitForResponse(message, (msg) => msg.type === 'join_stage_response');
        // If we were moved to stage, initialize the microphone
        if (joinStageResponse.placement === 'stage') {
            await this.audioManager?.initMic();
        }
        return joinStageResponse;
    }
    /**
     * Moderate the stage: accept/deny requests, kick users, or change stage size
     * Only users currently on stage can call this.
     */
    async moderateStage(channelId, action) {
        console.log('[LiveClient ws>] moderateStage', action.type);
        this.ensureConnected();
        const message = {
            type: 'moderate_stage',
            channelId,
            action,
            nonce: this.getNonce(),
        };
        return this.sendAndWaitForResponse(message, (msg) => msg.type === 'moderate_stage_response');
    }
    /**
     * Send a ping message for keep-alive
     */
    async ping() {
        this.ensureConnected();
        const message = {
            type: 'ping',
            timestamp: Date.now(),
            nonce: this.getNonce(),
        };
        return this.sendAndWaitForResponse(message, (msg) => msg.type === 'pong');
    }
    /**
     * Request the server to restore device state (subscriptions)
     * The server will respond with the count of spaces being restored,
     * then send live_space_summary messages for each subscription.
     */
    async restoreState() {
        this.ensureConnected();
        const message = {
            type: 'restore_state',
            nonce: this.getNonce(),
        };
        const response = await this.sendAndWaitForResponse(message, (msg) => msg.type === 'restore_state_response');
        console.log(`[LiveClient ws>] Restoring ${response.restoringCount} subscriptions`);
        return response;
    }
    /**
     * Publish an audio track
     */
    async publishAudioTrack(trackId) {
        this.ensureConnected();
        const message = {
            type: 'audio_publish_track',
            trackId,
            nonce: this.getNonce(),
        };
        return this.sendAndWaitForResponse(message, (msg) => msg.type === 'audio_publish_track_response');
    }
    /**
     * Register a handler for a specific message type
     */
    onMessageType(type, handler) {
        if (!this.messageHandlers.has(type)) {
            this.messageHandlers.set(type, new Set());
        }
        this.messageHandlers.get(type).add(handler);
        // Return unsubscribe function
        return () => {
            this.messageHandlers.get(type)?.delete(handler);
        };
    }
    onMessage(handler) {
        return this.onMessageType('*', handler);
    }
    /**
     * Get the current connection state
     */
    getState() {
        return this.state$.value;
    }
    /**
     * Clear the last error
     */
    clearError() {
        this.updateState({ lastError: undefined });
    }
    // Private methods
    getNonce() {
        return this._nonce++;
    }
    isSignerContext(wallet) {
        return (wallet &&
            typeof wallet === 'object' &&
            'signerPrivateKey' in wallet &&
            'creatorAddress' in wallet);
    }
    /**
     * Create an authentication ticket by signing the request and encoding it as base64
     */
    async getTicket() {
        const payload = {
            spaceId: this.options.spaceId,
            deviceId: this.options.deviceId,
        };
        const timestamp = Date.now();
        const domain = this.options.domain || 'localhost';
        const message = await createSignatureMessage({ payload, timestamp, domain });
        let signature;
        let delegateSig;
        let delegateExpiryEpochMs = 0;
        let userId;
        if (this.isSignerContext(this.options.wallet)) {
            // Handle SignerContext - delegate wallet case
            const signerContext = this.options.wallet;
            const signerPrivateKey = signerContext.signerPrivateKey();
            const delegateWallet = new ethers.Wallet('0x' + signerPrivateKey);
            signature = await delegateWallet.signMessage(message);
            userId = ethers.utils.getAddress(bin_toHexString(signerContext.creatorAddress));
            if (signerContext.delegateSig) {
                delegateSig = bin_toHexString(signerContext.delegateSig);
            }
            if (signerContext.delegateExpiryEpochMs) {
                delegateExpiryEpochMs = Number(signerContext.delegateExpiryEpochMs);
            }
        }
        else {
            // Handle regular Wallet/Signer case
            signature = await this.options.wallet.signMessage(message);
            userId = await this.options.wallet.getAddress();
        }
        // Build the signed request object
        const signedRequest = { payload, signature, timestamp, domain };
        if (delegateSig) {
            signedRequest.delegateSig = delegateSig;
            signedRequest.delegateExpiryEpochMs = delegateExpiryEpochMs;
        }
        // Encode the signed request as base64 - this becomes the "ticket"
        const ticket = btoa(JSON.stringify(signedRequest));
        return {
            ticket,
            userId,
        };
    }
    setupWebSocketHandlers() {
        if (!this.ws)
            return;
        this.ws.addEventListener('message', (event) => {
            try {
                const msg = JSON.parse(event.data);
                this.handleMessage(msg);
            }
            catch (error) {
                console.error('[LiveClient ws>] Failed to parse message:', error);
            }
        });
        this.ws.addEventListener('open', () => {
            console.log('[LiveClient ws>] WebSocket opened');
            this.updateState({
                connectionStatus: 'connected',
                isReconnecting: false,
            });
        });
        this.ws.addEventListener('close', () => {
            console.log(`[LiveClient ws>] WebSocket closed shouldReconnect: ${this.ws?.shouldReconnect === true}`);
            if (this.ws?.shouldReconnect) {
                // aellis 12/2025 there's a gap here where our websocket can reconnect on it's own
                // but we don't call restoreState() to reconcile any missed messages...
                // todo
                this.updateState({
                    connectionStatus: 'connecting',
                    isReconnecting: true,
                });
            }
            else {
                this.updateState({
                    ready: false,
                    connectionStatus: 'disconnected',
                    deviceState: undefined,
                    devices: [],
                });
            }
        });
        this.ws.addEventListener('error', (event) => {
            console.error(`[LiveClient ws>] WebSocket error, shouldReconnect:  ${this.ws?.shouldReconnect === true}`, event);
            if (!this.ws?.shouldReconnect) {
                this.updateState({
                    connectionStatus: 'error',
                    lastError: {
                        type: 'error_response',
                        code: LiveErrorCode.INTERNAL_ERROR,
                        message: `WebSocket error: ${event.message}`,
                        timestamp: Date.now(),
                        nonce: this.getNonce(),
                    },
                });
            }
        });
    }
    handleMessage(msg) {
        // Update state based on message type
        switch (msg.type) {
            case 'subscriptions_response':
                // handled in updateSubscriptions()
                break;
            case 'connect_response':
                // handled in connect()
                break;
            case 'join_channel_response':
                // handled in joinChannel()
                break;
            case 'leave_channel_response':
                // handled in leaveChannel()
                break;
            case 'leave_stage_response':
                // handled in leaveStage()
                break;
            case 'join_stage_response':
                // handled in joinStage()
                break;
            case 'moderate_stage_response':
                // handled in moderateStage()
                break;
            case 'restore_state_response':
                // handled in restoreState()
                break;
            case 'pong':
                break;
            case 'live_space_summary':
                this.handleLiveSpaceSummary(msg);
                break;
            case 'live_space_update':
                this.handleLiveSpaceUpdate(msg);
                break;
            case 'live_channel_update':
                this.handleLiveChannelUpdate(msg);
                break;
            case 'live_user_state':
                this.handleUserStateMessage(msg);
                break;
            case 'error_response':
                // handled in sendAndWaitForResponse
                break;
            // Audio messages - just pass through to handlers
            case 'audio_publish_track_response':
                console.log('[LiveClient ws>] Audio publish track response:', msg);
                break;
            case 'audio_tracks_published':
                this.handleAudioTracksPublished(msg);
                break;
            case 'audio_tracks_removed':
                this.handleAudioTracksRemoved(msg);
                break;
            case 'audio_error_response':
                console.log('[LiveClient ws>] Audio error response:', msg);
                // handled in sendAndWaitForResponse
                break;
            case 'placement_change':
                this.handlePlacementChange(msg);
                break;
            case 'session_transferred':
                this.handleSessionTransferred(msg);
                break;
            default:
                checkNever(msg);
        }
        // Notify registered handlers for specific message type
        const handlers = this.messageHandlers.get(msg.type);
        if (handlers) {
            handlers.forEach((handler) => handler(msg));
        }
        // Also notify wildcard handlers
        const wildcardHandlers = this.messageHandlers.get('*');
        if (wildcardHandlers) {
            wildcardHandlers.forEach((handler) => handler(msg));
        }
    }
    handleSubscriptionsResponse(msg) {
        const subscribedSpaces = { ...this.state$.value.spaces };
        // Subscribed spaces will have sent space summaries, so should exist in the state
        console.log('[LiveClient ws>] Subscriptions response subscribed:', msg.subscribed?.map((s) => s.spaceId).join(', '));
        console.log('[LiveClient ws>] Subscriptions response unsubscribed:', msg.unsubscribed?.map((s) => s.spaceId).join(', '));
        // Remove unsubscribed spaces
        if (msg.unsubscribed) {
            msg.unsubscribed.forEach((result) => {
                delete subscribedSpaces[result.spaceId];
            });
        }
        console.log('[LiveClient ws>] Subscriptions response:', subscribedSpaces, subscribedSpaces);
        this.updateState({ spaces: subscribedSpaces });
    }
    handleLiveSpaceSummary(msg) {
        console.log('[LiveClient ws>] Live space summary:', msg);
        const subscribedSpaces = { ...this.state$.value.spaces };
        const existingSpace = subscribedSpaces[msg.spaceId];
        // For space summary, we apply state and channels if they're newer
        const newState = !existingSpace || msg.summary.state.updatedAt > existingSpace.state.updatedAt
            ? msg.summary.state
            : existingSpace.state;
        // Merge channels, keeping newer versions of each
        const mergedChannels = { ...(existingSpace?.channels ?? {}) };
        for (const [channelId, channelState] of Object.entries(msg.summary.channels)) {
            const existingChannel = mergedChannels[channelId];
            if (!existingChannel || channelState.updatedAt > existingChannel.updatedAt) {
                mergedChannels[channelId] = channelState;
            }
        }
        subscribedSpaces[msg.spaceId] = {
            state: newState,
            channels: mergedChannels,
        };
        this.updateState({ spaces: subscribedSpaces });
    }
    handleLiveSpaceUpdate(msg) {
        console.log('[LiveClient ws>] Live space update:', msg);
        const subscribedSpaces = { ...this.state$.value.spaces };
        const existingSpace = subscribedSpaces[msg.spaceId];
        // Only apply if the new state is newer than or equal to existing state
        if (existingSpace && msg.state.updatedAt <= existingSpace.state.updatedAt) {
            console.log('[LiveClient ws>] Ignoring stale space update:', msg.state.updatedAt, '<', existingSpace.state.updatedAt);
            return;
        }
        subscribedSpaces[msg.spaceId] = {
            ...(existingSpace ?? EMPTY_SPACE_STATE),
            state: msg.state,
        };
        this.updateState({ spaces: subscribedSpaces });
    }
    handleLiveChannelUpdate(msg) {
        const subscribedSpaces = { ...this.state$.value.spaces };
        const existingSpace = subscribedSpaces[msg.spaceId];
        const existingChannel = existingSpace?.channels?.[msg.channelId];
        // Only apply if the new channel state is newer than or equal to existing state
        if (existingChannel && msg.channelState.updatedAt <= existingChannel.updatedAt) {
            console.log('[LiveClient ws>] Ignoring stale channel update:', msg.channelState.updatedAt, '<', existingChannel.updatedAt);
            return;
        }
        subscribedSpaces[msg.spaceId] = {
            ...(existingSpace ?? EMPTY_SPACE_STATE),
            channels: {
                ...(existingSpace?.channels ?? {}),
                [msg.channelId]: msg.channelState,
            },
        };
        this.updateState({ spaces: subscribedSpaces });
    }
    handleUserStateMessage(msg) {
        this.handleDevicesUpdate(msg);
    }
    handleAudioTracksPublished(msg) {
        console.log('[LiveClient ws>] Audio tracks published:', msg);
        if (msg.spaceId === this.audioManager?.spaceId &&
            msg.channelId === this.audioManager?.channelId) {
            this.audioManager?.subscribeTracks(msg.trackInfos);
        }
    }
    handleAudioTracksRemoved(msg) {
        console.log('[LiveClient ws>] Audio tracks removed:', msg);
        if (msg.spaceId === this.audioManager?.spaceId &&
            msg.channelId === this.audioManager?.channelId) {
            this.audioManager?.unsubscribeTracks(msg.trackIds);
        }
    }
    handlePlacementChange(msg) {
        console.log('[LiveClient ws>] Placement change:', msg);
        // Verify the message is for this device and current channel
        if (msg.deviceId !== this.options.deviceId) {
            console.log('[LiveClient ws>] Ignoring placement change for different device:', msg.deviceId);
            return;
        }
        if (msg.spaceId !== this.audioManager?.spaceId ||
            msg.channelId !== this.audioManager?.channelId) {
            console.log('[LiveClient ws>] Ignoring placement change for different channel:', msg.spaceId, msg.channelId);
            return;
        }
        // Update mic based on new placement
        if (msg.placement === 'stage') {
            console.log('[LiveClient ws>] Promoted to stage, initializing mic');
            this.audioManager?.initMic();
        }
        else {
            // listener or stage_request - stop the mic
            console.log('[LiveClient ws>] Moved off stage, stopping mic');
            this.audioManager?.stopMic();
        }
    }
    handleSessionTransferred(msg) {
        console.log('[LiveClient ws>] Session transferred to another device:', msg.toDeviceId);
        // Clean up audio manager
        this.audioManager?.destroy();
        this.audioManager = undefined;
        // Update state to reflect we're no longer in a channel
        this.updateState({
            audioState: undefined,
            deviceState: { type: 'idle', deviceId: this.options.deviceId },
        });
    }
    handleDevicesUpdate(msg) {
        console.log('[LiveClient ws>] Devices update:', msg.devices);
        const thisDeviceState = msg.devices.find((s) => s.deviceId === this.options.deviceId);
        this.updateState({
            deviceState: thisDeviceState,
            devices: msg.devices,
        });
    }
    sendAndWaitForResponse(message, filter, timeoutMs = 30000) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                off();
                this.updateState({
                    lastError: {
                        type: 'error_response',
                        code: LiveErrorCode.INTERNAL_ERROR,
                        message: `Timeout waiting for response to ${message.type}`,
                        timestamp: Date.now(),
                        nonce: message.nonce,
                    },
                });
                reject(new Error(`Timeout waiting for response to ${message.type}`));
            }, timeoutMs);
            const off = this.onMessage((msg) => {
                if (filter(msg) && msg.nonce === message.nonce) {
                    clearTimeout(timeout);
                    off();
                    resolve(msg);
                }
                if (msg.type === 'error_response' &&
                    (msg.nonce === message.nonce || msg.nonce === -1)) {
                    clearTimeout(timeout);
                    off();
                    this.updateState({
                        lastError: msg,
                    });
                    reject(new Error(msg.message));
                }
                if (msg.type === 'audio_error_response' &&
                    (msg.nonce === message.nonce || msg.nonce === -1)) {
                    clearTimeout(timeout);
                    off();
                    this.updateState({
                        lastError: msg,
                    });
                    reject(new Error(msg.message));
                }
            });
            // Send the message
            this.ws?.send(JSON.stringify(message));
        });
    }
    ensureConnected() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            throw new Error('Not connected to live worker');
        }
    }
    updateState(updates) {
        this.state$.setValue({ ...this.state$.value, ...updates });
    }
}
//# sourceMappingURL=LiveClient.js.map